import { useState, useEffect, useRef } from 'react';
import { TicketOut, EventStatus } from '../api/types';
import { liveStreamManager } from '../services/CatalogStreamManager';

export function useLiveScores(tickets: TicketOut[]) {
  const [liveScores, setLiveScores] = useState<Record<string, EventStatus>>({});
  const activeEventIdsRef = useRef<Set<string>>(new Set());

  // Sayfa açılır açılmaz bağlantıyı kurup verileri çekmeye başlaması için abonelik ekliyoruz
  useEffect(() => {
    const unsub = liveStreamManager.subscribe(() => {});
    return () => unsub();
  }, []);

  useEffect(() => {
    // Track live scores for every leg of an open ticket that isn't already
    // settled. The leg's result_status is 'open' while the match is in play
    // (not just 'not_resulted'/'pending'), so anything not in this settled set
    // — including undefined — must be subscribed, otherwise the live stream
    // never feeds these events and the ticket detail can't show the minute.
    const SETTLED = new Set(['won', 'win', 'lost', 'lose', 'void', 'voided', 'cancelled', 'canceled', 'refunded']);
    const ids = new Set<string>();
    tickets.forEach(t => {
      if (t.status === 'open' || t.status === 'accepted') {
        t.legs?.forEach(leg => {
          if (!leg.result_status || !SETTLED.has(leg.result_status)) {
            ids.add(leg.event_id);
          }
        });
      }
    });
    activeEventIdsRef.current = ids;
  }, [tickets]);

  useEffect(() => {
    const hasOpenTickets = tickets.some(t => t.status === 'open' || t.status === 'accepted');

    if (hasOpenTickets) {
      const unsubscribe = liveStreamManager.subscribe((upserts, deletes) => {
        setLiveScores(prev => {
          let next = { ...prev };
          let changed = false;

          for (const eventId of deletes) {
            if (next[eventId]) {
              delete next[eventId];
              changed = true;
            }
          }

          for (const ev of upserts) {
            if (activeEventIdsRef.current.has(ev.event_id) && ev.status) {
              const existing = next[ev.event_id];
              const incomingStatus = ev.status;
              if (!existing || existing.home_score !== incomingStatus.home_score || existing.away_score !== incomingStatus.away_score || existing.clock !== incomingStatus.clock) {
                next[ev.event_id] = incomingStatus;
                changed = true;
              }
            }
          }

          return changed ? next : prev;
        });
      });

      return () => unsubscribe();
    }
  }, [tickets.some(t => t.status === 'open' || t.status === 'accepted')]);

  return liveScores;
}

